import { toTypedRxJsonSchema } from 'rxdb';
import type { ExtractDocumentTypeFromTypedRxJsonSchema, RxJsonSchema } from 'rxdb';

export interface PackageItemType {
  id?: string;
  service_id: string;
  session_count: number;
}

export interface CustomerPackageDetailType {
  id: string;
  service_id: string;
  total_sessions: number;
  used_sessions: number;
  remaining_sessions: number;
}

export interface CustomerAttentionType {
  id: string;
  service_id: string;
  staff_id: string;
  branch_id: string;
  attention_date: string;
  status: 'completed' | 'cancelled';
  commission_amount: number;
  notes?: string | null;
}

// 1. Package Schema (Combo / Package Template with embedded items breakdown)
export const packageSchemaLiteral = {
  title: 'package',
  version: 0,
  primaryKey: 'id',
  type: 'object',
  properties: {
    id: { type: 'string', maxLength: 100 },
    name: { type: 'string', maxLength: 200 },
    description: { type: ['string', 'null'] },
    currency_price: { type: 'number' },
    branch_id: { type: ['string', 'null'], maxLength: 100 },
    is_active: { type: 'boolean' },
    items: {
      type: 'array',
      items: {
        type: 'object',
        properties: {
          id: { type: 'string' },
          service_id: { type: 'string' },
          session_count: { type: 'number' },
        },
        required: ['service_id', 'session_count'],
      },
    },
    // --- Control ---
    created_at: { type: 'string', maxLength: 50 },
    updated_at: { type: 'string', maxLength: 50 },
    is_deleted: { type: 'boolean' },
    
    last_modified_by: { type: ['string', 'null'], maxLength: 100 },
  },
  required: ['id', 'name', 'currency_price', 'is_active', 'items', 'updated_at', 'is_deleted'],
  indexes: ['is_deleted', 'updated_at'],
} as const;

const packageSchemaTyped = toTypedRxJsonSchema(packageSchemaLiteral);
export type PackageDocType = ExtractDocumentTypeFromTypedRxJsonSchema<typeof packageSchemaTyped>;
export const packageSchema: RxJsonSchema<PackageDocType> = packageSchemaLiteral;


// 2. CustomerPackage Schema (Client Purchased Package with embedded details & attentions)
export const customerPackageSchemaLiteral = {
  title: 'customer_packages',
  version: 0,
  primaryKey: 'id',
  type: 'object',
  properties: {
    id: { type: 'string', maxLength: 100 },
    customer_id: { type: 'string', maxLength: 100 },
    package_id: { type: ['string', 'null'], maxLength: 100 },
    transaction_id: { type: ['string', 'null'], maxLength: 100 },
    package_name: { type: 'string', maxLength: 200 },
    purchase_date: { type: 'string', maxLength: 50 },
    status: { type: 'string', enum: ['active', 'exhausted', 'cancelled'], maxLength: 20 },
    notes: { type: ['string', 'null'] },
    details: {
      type: 'array',
      items: {
        type: 'object',
        properties: {
          id: { type: 'string' },
          service_id: { type: 'string' },
          total_sessions: { type: 'number' },
          used_sessions: { type: 'number' },
          remaining_sessions: { type: 'number' },
        },
        required: ['id', 'service_id', 'total_sessions', 'used_sessions', 'remaining_sessions'],
      },
    },
    attentions: {
      type: 'array',
      items: {
        type: 'object',
        properties: {
          id: { type: 'string' },
          service_id: { type: 'string' },
          staff_id: { type: ['string', 'null'] },
          branch_id: { type: 'string' },
          attention_date: { type: ['string', 'null'] },
          status: { type: 'string' },
          commission_amount: { type: ['number', 'null'] },
          notes: { type: ['string', 'null'] },
        },
        required: ['id', 'service_id', 'branch_id', 'status'],
      },
    },
    // --- Control ---
    created_at: { type: 'string', maxLength: 50 },
    updated_at: { type: 'string', maxLength: 50 },
    is_deleted: { type: 'boolean' },
    
    last_modified_by: { type: ['string', 'null'], maxLength: 100 },
  },
  required: [
    'id',
    'customer_id',
    'package_name',
    'purchase_date',
    'status',
    'details',
    'attentions',
    'updated_at',
    'is_deleted',
  ],
  indexes: ['is_deleted', 'customer_id', 'status', 'updated_at'],
} as const;

const customerPackageSchemaTyped = toTypedRxJsonSchema(customerPackageSchemaLiteral);
export type CustomerPackageDocType = ExtractDocumentTypeFromTypedRxJsonSchema<typeof customerPackageSchemaTyped>;
export const customerPackageSchema: RxJsonSchema<CustomerPackageDocType> = customerPackageSchemaLiteral;
